import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Bell, Settings, User, Menu, X, FileText, DollarSign, Calendar, Map, Search, MessageCircle, HelpCircle, Phone, LogOut, ChevronDown, Heart, Brain } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { useRealTimeNotifications } from '../hooks/useRealTimeNotifications';
import { isLandlord, getPrimaryRole } from '../utils/roleHelpers';
interface ImprovedUniversalHeaderProps {
  title?: string;
  showBackButton?: boolean;
  customBackPath?: string;
  className?: string;
  rightActions?: React.ReactNode;
}
const ImprovedUniversalHeader: React.FC<ImprovedUniversalHeaderProps> = ({
  title,
  showBackButton = true,
  customBackPath,
  className,
  rightActions
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    profile,
    logout
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    stats
  } = useRealTimeNotifications();
  const handleBack = () => {
    if (customBackPath) {
      navigate(customBackPath);
    } else if (location.pathname.startsWith('/chat')) {
      navigate(isLandlord(profile) ? '/landlord-messages' : '/messages');
    } else if (isLandlord(profile)) {
      navigate('/landlord-dashboard');
    } else {
      navigate('/dashboard');
    }
  };
  const handleSignOut = async () => {
    try {
      setMobileMenuOpen(false); // Close mobile menu first
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  const getPageTitle = () => {
    if (title) return title;
    const path = location.pathname;
    switch (path) {
      case '/discover':
        return 'Discover Properties';
      case '/matches':
        return 'Your Matches';
      case '/messages':
        return 'Messages';
      case '/profile':
        return 'Your Profile';
      case '/settings':
        return 'Settings';
      case '/add-listing':
        return 'Add New Listing';
      case '/landlord-tours':
        return 'Scheduled Tours';
      case '/dashboard':
        return 'Dashboard';
      default:
        return 'Flamingo';
    }
  };
  const getFirstName = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ')[0];
    }
    return user?.email?.split('@')[0] || 'User';
  };
  const rentalToolsFeatures = [{
    name: 'My Application',
    path: '/my-application',
    icon: FileText
  }, {
    name: 'Price Guide',
    path: '/rental-guide',
    icon: DollarSign
  }, {
    name: 'Timeline',
    path: '/timeline',
    icon: Calendar
  }];
  const discoveryFeatures = [{
    name: 'Map View',
    path: '/map',
    icon: Map
  }, {
    name: 'Discover',
    path: '/discover',
    icon: Search
  }, {
    name: 'Matches',
    path: '/matches',
    icon: Heart
  }];
  const communicationFeatures = [{
    name: 'Messages',
    path: '/messages',
    icon: MessageCircle
  }, {
    name: 'Fiona',
    path: '/chat',
    icon: Brain
  }];
  const supportFeatures = [{
    name: 'Profile',
    path: '/profile',
    icon: User
  }, {
    name: 'Help Center',
    path: '/help',
    icon: HelpCircle
  }, {
    name: 'Contact Us',
    path: '/contact-us',
    icon: Phone
  }, {
    name: 'Settings',
    path: '/settings',
    icon: Settings
  }];
  return <header className={cn("bg-slate-900/98 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50", className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left section */}
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            {showBackButton && <Button variant="ghost" size="sm" onClick={handleBack} className="p-2 hover:bg-slate-800/50 rounded-xl transition-colors text-white">
                <ArrowLeft className="w-4 h-4" />
              </Button>}
            
            <div className="flex items-center space-x-3 min-w-0">
              <img alt="Flamingo" className="h-8 w-auto cursor-pointer flex-shrink-0" onClick={handleBack} src="/lovable-uploads/d981c4c2-888d-4322-9d83-e527475f379f.png" />
              
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Custom Right Actions */}
            {rightActions}
            
            {/* Desktop Navigation Dropdown */}
            <div className="hidden md:flex items-center space-x-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center space-x-2 p-2 hover:bg-slate-800/50 rounded-xl transition-colors text-white">
                    <Menu className="w-4 h-4" />
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-slate-900/95 backdrop-blur-lg border-slate-700/50 shadow-lg" sideOffset={8}>
                  <DropdownMenuLabel className="text-white">Rental Tools</DropdownMenuLabel>
                  {rentalToolsFeatures.map(feature => {
                  const IconComponent = feature.icon;
                  return <DropdownMenuItem key={feature.path} onClick={() => navigate(feature.path)} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-800/50 text-white">
                        <IconComponent className="w-4 h-4" />
                        <span>{feature.name}</span>
                      </DropdownMenuItem>;
                })}
                  
                  <DropdownMenuSeparator className="bg-slate-700/50" />
                  
                  <DropdownMenuLabel className="text-white">Discovery</DropdownMenuLabel>
                  {discoveryFeatures.map(feature => {
                  const IconComponent = feature.icon;
                  return <DropdownMenuItem key={feature.path} onClick={() => navigate(feature.path)} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-800/50 text-white">
                        <IconComponent className="w-4 h-4" />
                        <span>{feature.name}</span>
                      </DropdownMenuItem>;
                })}
                  
                  <DropdownMenuSeparator className="bg-slate-700/50" />
                  
                  <DropdownMenuLabel className="text-white">Communication</DropdownMenuLabel>
                  {communicationFeatures.map(feature => {
                  const IconComponent = feature.icon;
                  return <DropdownMenuItem key={feature.path} onClick={() => navigate(feature.path)} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-800/50 text-white">
                        <IconComponent className="w-4 h-4" />
                        <span>{feature.name}</span>
                      </DropdownMenuItem>;
                })}
                  
                  <DropdownMenuSeparator className="bg-slate-700/50" />
                  
                  <DropdownMenuLabel className="text-white">Support</DropdownMenuLabel>
                  {supportFeatures.map(feature => {
                  const IconComponent = feature.icon;
                  return <DropdownMenuItem key={feature.path} onClick={() => navigate(feature.path)} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-800/50 text-white">
                        <IconComponent className="w-4 h-4" />
                        <span>{feature.name}</span>
                      </DropdownMenuItem>;
                })}
                  
                  <DropdownMenuSeparator className="bg-slate-700/50" />
                  
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center space-x-3 cursor-pointer hover:bg-red-900/20 text-red-400">
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* User Profile */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-white truncate max-w-32">
                  {getFirstName()}
                </p>
                <p className="text-xs text-slate-400 capitalize">
                  {getPrimaryRole(profile) || 'User'}
                </p>
              </div>
            </div>
            
            {/* Mobile Menu Button */}
            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 hover:bg-slate-800/50 rounded-xl transition-colors text-white">
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
            
            {/* Desktop Sign Out */}
            <Button variant="outline" size="sm" onClick={handleSignOut} className="hidden lg:flex text-xs px-3 py-2 bg-slate-800/50 border-slate-600/50 text-slate-200 hover:bg-slate-700/50 rounded-xl transition-colors">
              Sign Out
            </Button>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        {mobileMenuOpen && <div className="md:hidden border-t border-slate-700/50 py-3 max-h-[80vh] overflow-y-auto">
            {/* Rental Tools Section */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-2 px-2">
                Rental Tools
              </h3>
              <div className="space-y-1">
                {rentalToolsFeatures.map(feature => {
              const IconComponent = feature.icon;
              return <Button key={feature.path} variant="ghost" size="sm" className="w-full justify-start space-x-3 h-11 px-3 hover:bg-slate-800/50 rounded-xl transition-colors text-white" onClick={() => handleMenuItemClick(feature.path)}>
                      <IconComponent className="w-4 h-4 text-white" />
                      <span className="text-left font-medium">{feature.name}</span>
                    </Button>;
            })}
              </div>
            </div>

            <Separator className="h-px bg-slate-600/50 w-full mx-auto mb-3" />

            {/* Discovery Section */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-2 px-2">
                Discovery
              </h3>
              <div className="space-y-1">
                {discoveryFeatures.map(feature => {
              const IconComponent = feature.icon;
              return <Button key={feature.path} variant="ghost" size="sm" className="w-full justify-start space-x-3 h-11 px-3 hover:bg-slate-800/50 rounded-xl transition-colors text-white" onClick={() => handleMenuItemClick(feature.path)}>
                      <IconComponent className="w-4 h-4 text-white" />
                      <span className="text-left font-medium">{feature.name}</span>
                    </Button>;
            })}
              </div>
            </div>

            <Separator className="h-px bg-slate-600/50 w-full mx-auto mb-3" />

            {/* Communication Section */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-2 px-2">
                Communication
              </h3>
              <div className="space-y-1">
                {communicationFeatures.map(feature => {
              const IconComponent = feature.icon;
              return <Button key={feature.path} variant="ghost" size="sm" className="w-full justify-start space-x-3 h-11 px-3 hover:bg-slate-800/50 rounded-xl transition-colors text-white" onClick={() => handleMenuItemClick(feature.path)}>
                      <IconComponent className="w-4 h-4 text-white" />
                      <span className="text-left font-medium">{feature.name}</span>
                    </Button>;
            })}
              </div>
            </div>

            <Separator className="h-px bg-slate-600/50 w-full mx-auto mb-3" />

            {/* Support Section */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-2 px-2">
                Support
              </h3>
              <div className="space-y-1">
                {supportFeatures.map(feature => {
              const IconComponent = feature.icon;
              return <Button key={feature.path} variant="ghost" size="sm" className="w-full justify-start space-x-3 h-11 px-3 hover:bg-slate-800/50 rounded-xl transition-colors text-white" onClick={() => handleMenuItemClick(feature.path)}>
                      <IconComponent className="w-4 h-4 text-white" />
                      <span className="text-left font-medium">{feature.name}</span>
                    </Button>;
            })}
              </div>
            </div>

            <Separator className="h-px bg-slate-600/50 w-full mx-auto mb-3" />

            {/* Sign Out */}
            <div className="mt-3">
              <Button variant="ghost" size="sm" className="w-full justify-start space-x-3 h-12 px-3 hover:bg-red-900/20 text-red-400 rounded-xl transition-colors" onClick={() => {
            handleSignOut();
            setMobileMenuOpen(false);
          }}>
                <LogOut className="w-4 h-4 text-red-400" />
                <span className="text-left font-medium">Sign Out</span>
              </Button>
            </div>
          </div>}
      </div>
    </header>;
};
export default ImprovedUniversalHeader;